import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api-client.js';
import { useResource } from '../lib/use-resource.js';
import { Badge, ButtonLink, Card, ProgressBar } from '../ui/kit.js';

/**
 * Downloads still under way, if there are any.
 *
 * A channel import takes hours and keeps going after its own page is closed,
 * so the dashboard is where a parent finds out it is still running. Absent
 * entirely when nothing is: a card saying "no downloads" is a card nobody needs.
 *
 * Refreshed only while something is running, and not while the tab is hidden —
 * the same courtesy the live block extends to the server.
 */

/** Downloads are minutes each; the live block's ten seconds is ample here too. */
const REFRESH_INTERVAL_MS = 10_000;

export const RunningImports = () => {
  const { t } = useTranslation();
  const imports = useResource('imports', () => apiClient.imports.list());
  const { reload } = imports;

  // `data` survives a reload, so the bars do not blink between refreshes.
  const running = (imports.resource.data?.jobs ?? []).filter((job) => job.status === 'RUNNING');
  const hasRunning = running.length > 0;

  useEffect(() => {
    if (!hasRunning) {
      return;
    }
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        reload();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [hasRunning, reload]);

  if (!hasRunning) {
    return null;
  }

  return (
    <Card
      title={t('overview.importing')}
      aside={<Badge tone="amber">{t('import.item.DOWNLOADING')}</Badge>}
    >
      {running.map((job) => (
        <div key={job.id} className="overview-import">
          <div className="imp-head">
            <b>{job.sourceTitle}</b>
            <span className="num">
              {t('import.status.RUNNING', {
                completed: job.completed,
                total: job.total,
                failed: job.failed,
              })}
            </span>
          </div>
          <ProgressBar
            value={job.total === 0 ? 0 : (job.completed + job.failed) / job.total}
            label={job.sourceTitle}
          />
        </div>
      ))}
      <div className="overview-import-actions">
        <ButtonLink variant="secondary" small to="/library">
          {t('import.goToLibrary')}
        </ButtonLink>
      </div>
    </Card>
  );
};
